import React from 'react';
import PropTypes from 'prop-types';
import { FormControl, Select, MenuItem } from '@mui/material';
import { QUERY_STATUSES } from 'src/constants/config';
import { queryApi } from 'src/api/queryApi';
import toast from 'react-hot-toast';

const QueryStatus = ({ query, mutate, size = 'small' }) => {
  const [status, setStatus] = React.useState(query.status || 'new');

  // Add useEffect to sync status with query.status changes
  React.useEffect(() => {
    setStatus(query.status || 'new');
  }, [query.status]);

  // Check if status should be visible based on the original visibility logic
  const isVisible = React.useMemo(() => {
    if (query.status === 'new') return false;
    if (query.status === 'repurposed') return false;
    return true;
  }, [query.status]);

  const handleStatusChange = async (event) => {
    const newStatus = event.target.value;
    setStatus(newStatus);
    try {
      const response = await queryApi.updateQuery(query.id, newStatus);
      if (response.status === 'Query Updated') {
        if (mutate) mutate();
        toast.success('Status updated successfully');
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
      setStatus(query.status || 'new');
    }
  };

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  if (!isVisible) return null;

  return (
    <FormControl fullWidth size={size} onClick={handleClick}>
      <Select
        value={status}
        onChange={handleStatusChange}
        onClick={handleClick}
        sx={{
          bgcolor: 'white',
          '& .MuiSelect-select': {
            py: 1,
            color: (theme) => theme.palette[QUERY_STATUSES[status]?.color || 'default'].main,
          },
        }}
      >
        {Object.entries(QUERY_STATUSES)
          .filter(([_, statusConfig]) => !statusConfig.hideInOptions)
          .filter(([value]) => {
            // For draft queries, only show draft option
            if (query.status === 'draft') {
              return value === 'draft';
            }
            return true;
          })
          .map(([value, { label, color }]) => (
            <MenuItem
              key={value}
              value={value}
              onClick={handleClick}
              sx={{
                color: (theme) => theme.palette[color].main,
              }}
            >
              {label}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};

QueryStatus.propTypes = {
  query: PropTypes.object.isRequired,
  mutate: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['small', 'medium']),
};

export default QueryStatus;
